/**
 * Scenario Contract — Zod Runtime Validation Schemas
 *
 * Mirrors the TypeScript types in types/scenario-contract.ts.
 * Every contract entering the system MUST pass through these schemas.
 */
import { z } from 'zod'

// ════════════════════════════════════════════════════════════
// Shared Enums & Atoms
// ════════════════════════════════════════════════════════════

export const WheelTargetSchema = z.enum(['front', 'rear', 'both'])
export const BumpShapeSchema = z.enum(['half_sine', 'rectangular', 'triangular'])
export const IsoRoughnessClassSchema = z.enum(['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'])
export const InterpolationMethodSchema = z.enum(['linear', 'step', 'spline'])
export const EcuModeSchema = z.enum(['comfort', 'sport', 'rain', 'off-road', 'track'])
export const ReplayTransformSchema = z.enum(['identity', 'negate', 'deg_to_rad', 'scale'])
export const KPIAggregationSchema = z.enum(['max', 'min', 'mean', 'rms', 'settling_time', 'overshoot'])
export const KPIOperatorSchema = z.enum(['<', '<=', '>', '>=', '==', 'range'])
export const PrioritySchema = z.enum(['High', 'Medium', 'Low'])
export const SafetyLevelSchema = z.enum(['ASIL A', 'ASIL B', 'ASIL C', 'ASIL D', 'QM'])
export const TestEnvironmentSchema = z.enum(['SIL', 'HIL', 'Vehicle'])
export const PrimitiveSupportStatusSchema = z.enum(['supported', 'experimental', 'unsupported'])

// ════════════════════════════════════════════════════════════
// Road Primitives
// ════════════════════════════════════════════════════════════

export const RoadBumpParamsSchema = z.object({
  time_s: z.number().nonnegative(),
  amplitude_m: z.number().positive(),
  width_m: z.number().positive(),
  shape: BumpShapeSchema.optional(),
  wheel: WheelTargetSchema.optional(),
})

export const RoadSineParamsSchema = z.object({
  frequency_hz: z.number().positive(),
  amplitude_m: z.number().positive(),
  start_s: z.number().nonnegative(),
  end_s: z.number().nonnegative(),
  phase_deg: z.number().optional(),
  wheel: WheelTargetSchema.optional(),
}).refine(d => d.end_s > d.start_s, { message: 'end_s must be greater than start_s' })

export const RoadRandomRoughnessParamsSchema = z.object({
  iso_class: IsoRoughnessClassSchema,
  start_s: z.number().nonnegative(),
  end_s: z.number().nonnegative(),
  seed: z.number().int().optional(),
}).refine(d => d.end_s > d.start_s, { message: 'end_s must be greater than start_s' })

export const RoadStepParamsSchema = z.object({
  time_s: z.number().nonnegative(),
  amplitude_m: z.number(),
  rise_time_s: z.number().nonnegative().optional(),
  wheel: WheelTargetSchema.optional(),
})

export const RoadPotholeParamsSchema = z.object({
  time_s: z.number().nonnegative(),
  depth_m: z.number().positive(),
  length_m: z.number().positive(),
  shape: BumpShapeSchema.optional(),
  wheel: WheelTargetSchema.optional(),
})

export const ElevationPointSchema = z.object({
  time_s: z.number().nonnegative(),
  elevation_m: z.number(),
})

export const RoadCustomProfileParamsSchema = z.object({
  time_series: z.array(ElevationPointSchema).min(2),
  wheel: WheelTargetSchema.optional(),
  interpolation: InterpolationMethodSchema.optional(),
})

export const RoadPrimitiveSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('road.bump'), params: RoadBumpParamsSchema }),
  z.object({ type: z.literal('road.sine'), params: RoadSineParamsSchema }),
  z.object({ type: z.literal('road.random_roughness'), params: RoadRandomRoughnessParamsSchema }),
  z.object({ type: z.literal('road.step'), params: RoadStepParamsSchema }),
  z.object({ type: z.literal('road.pothole'), params: RoadPotholeParamsSchema }),
  z.object({ type: z.literal('road.custom_profile'), params: RoadCustomProfileParamsSchema }),
])

// ════════════════════════════════════════════════════════════
// Control Signals
// ════════════════════════════════════════════════════════════

export const KeypointSchema = z.object({
  time_s: z.number().nonnegative(),
  value: z.number(),
})

export const ControlSignalSchema = z.object({
  interpolation: InterpolationMethodSchema,
  keypoints: z.array(KeypointSchema).min(2),
}).refine(
  d => {
    for (let i = 1; i < d.keypoints.length; i++) {
      if (d.keypoints[i].time_s < d.keypoints[i - 1].time_s) return false
    }
    return true
  },
  { message: 'keypoints must be in non-decreasing time order' },
)

export const BrakeControlSignalSchema = z.object({
  interpolation: InterpolationMethodSchema,
  keypoints: z.array(KeypointSchema).min(2),
  split_front: z.number().min(0).max(1),
}).refine(
  d => {
    for (let i = 1; i < d.keypoints.length; i++) {
      if (d.keypoints[i].time_s < d.keypoints[i - 1].time_s) return false
    }
    return true
  },
  { message: 'keypoints must be in non-decreasing time order' },
)

export const StimulusControlsSchema = z.object({
  speed: ControlSignalSchema,
  steering: ControlSignalSchema,
  brake: BrakeControlSignalSchema,
  throttle: ControlSignalSchema,
})

// ════════════════════════════════════════════════════════════
// Events
// ════════════════════════════════════════════════════════════

export const ModeSwitchParamsSchema = z.object({
  mode: EcuModeSchema,
  transition_ms: z.number().nonnegative().optional(),
})

export const ParamSetSwitchParamsSchema = z.object({
  param_set_id: z.string().min(1),
  transition_ms: z.number().nonnegative().optional(),
})

export const RiderInputParamsSchema = z.object({
  action: z.string().min(1),
  magnitude: z.number().optional(),
})

export const DiscreteEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('event.mode_switch'), time_s: z.number().nonnegative(), params: ModeSwitchParamsSchema }),
  z.object({ type: z.literal('event.param_set_switch'), time_s: z.number().nonnegative(), params: ParamSetSwitchParamsSchema }),
  z.object({ type: z.literal('event.rider_input'), time_s: z.number().nonnegative(), params: RiderInputParamsSchema }),
])

// ════════════════════════════════════════════════════════════
// Fault Injections
// ════════════════════════════════════════════════════════════

export const SensorIdSchema = z.enum([
  'fork_position', 'rear_position', 'imu_accel', 'imu_gyro',
  'wheel_speed_front', 'wheel_speed_rear',
])

export const SensorFreezeParamsSchema = z.object({
  sensor_id: SensorIdSchema,
  duration_s: z.number().positive(),
  frozen_value: z.number().optional(),
})

export const OutOfRangeParamsSchema = z.object({
  sensor_id: SensorIdSchema,
  duration_s: z.number().positive(),
  value: z.number(),
  direction: z.enum(['high', 'low']).optional(),
})

export const ValveStuckParamsSchema = z.object({
  valve_id: z.string().min(1),
  position: z.enum(['open', 'closed', 'mid']),
  duration_s: z.number().positive(),
})

export const SignalDelayParamsSchema = z.object({
  signal_id: z.string().min(1),
  delay_ms: z.number().positive(),
  duration_s: z.number().positive(),
})

export const IntermittentParamsSchema = z.object({
  sensor_id: SensorIdSchema,
  pattern: z.enum(['dropout', 'noise_burst']),
  duration_s: z.number().positive(),
  frequency_hz: z.number().positive().optional(),
  noise_amplitude: z.number().positive().optional(),
})

export const FaultEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('fault.sensor_freeze'), time_s: z.number().nonnegative(), params: SensorFreezeParamsSchema }),
  z.object({ type: z.literal('fault.out_of_range'), time_s: z.number().nonnegative(), params: OutOfRangeParamsSchema }),
  z.object({ type: z.literal('fault.valve_stuck'), time_s: z.number().nonnegative(), params: ValveStuckParamsSchema }),
  z.object({ type: z.literal('fault.signal_delay'), time_s: z.number().nonnegative(), params: SignalDelayParamsSchema }),
  z.object({ type: z.literal('fault.intermittent'), time_s: z.number().nonnegative(), params: IntermittentParamsSchema }),
])

// ════════════════════════════════════════════════════════════
// Primitive Types
// ════════════════════════════════════════════════════════════

export const RoadPrimitiveTypeSchema = z.enum([
  'road.bump', 'road.sine', 'road.random_roughness',
  'road.step', 'road.pothole', 'road.custom_profile',
])

export const ControlPrimitiveTypeSchema = z.enum([
  'control.speed', 'control.steering', 'control.brake', 'control.throttle',
])

export const EventPrimitiveTypeSchema = z.enum([
  'event.mode_switch', 'event.param_set_switch', 'event.rider_input',
])

export const FaultPrimitiveTypeSchema = z.enum([
  'fault.sensor_freeze', 'fault.out_of_range', 'fault.valve_stuck',
  'fault.signal_delay', 'fault.intermittent',
])

export const PrimitiveTypeSchema = z.union([
  RoadPrimitiveTypeSchema,
  ControlPrimitiveTypeSchema,
  EventPrimitiveTypeSchema,
  FaultPrimitiveTypeSchema,
])

// ════════════════════════════════════════════════════════════
// Observables
// ════════════════════════════════════════════════════════════

export const ReplayMappingSchema = z.object({
  target: z.string().min(1),
  property: z.string().min(1),
  transform: ReplayTransformSchema,
  scale: z.number(),
})

export const ObservableChannelSchema = z.object({
  id: z.string().min(1),
  simulink_signal: z.string().min(1),
  unit: z.string().min(1),
  replay: ReplayMappingSchema.nullable(),
})

export const KPIDefinitionSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  channel: z.string().min(1),
  aggregation: KPIAggregationSchema,
  aggregation_params: z.record(z.string(), z.union([z.number(), z.string()])).optional(),
  operator: KPIOperatorSchema,
  threshold: z.number(),
  threshold_max: z.number().optional(),
  unit: z.string(),
  safety_level: SafetyLevelSchema,
  requirement_id: z.string(),
  obligation_id: z.string().optional(),
}).refine(
  d => d.operator !== 'range' || d.threshold_max !== undefined,
  { message: 'threshold_max is required when operator is "range"' },
)

// ════════════════════════════════════════════════════════════
// Meta
// ════════════════════════════════════════════════════════════

export const ScenarioMetaSchema = z.object({
  $schema: z.literal('moto-scenario/v1'),
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  duration_s: z.number().positive(),
  tags: z.array(z.string()),
  requirement_ids: z.array(z.string()),
  priority: PrioritySchema,
  safety_level: SafetyLevelSchema,
  environment: z.array(TestEnvironmentSchema).min(1),
  created_at: z.string().min(1),
  content_hash: z.string().optional(),
})

// ════════════════════════════════════════════════════════════
// Stimulus
// ════════════════════════════════════════════════════════════

export const StimulusSchema = z.object({
  road: z.array(RoadPrimitiveSchema),
  controls: StimulusControlsSchema,
})

// ════════════════════════════════════════════════════════════
// ECU
// ════════════════════════════════════════════════════════════

export const PlantParametersSchema = z.object({
  vehicle_mass_kg: z.number().positive(),
  cg_height_m: z.number().positive(),
  wheelbase_m: z.number().positive(),
  fork_max_travel_m: z.number().positive(),
  rear_max_travel_m: z.number().positive(),
  front_spring_k: z.number().positive(),
  front_damper_c: z.number().positive(),
  rear_spring_k: z.number().positive(),
  rear_damper_c: z.number().positive(),
  rear_compression_ratio: z.number().positive(),
})

export const EcuConfigurationSchema = z.object({
  controller_id: z.string().min(1),
  mode: EcuModeSchema,
  calibration: z.record(z.string(), z.union([z.number(), z.string()])),
  plant: PlantParametersSchema,
})

// ════════════════════════════════════════════════════════════
// Events
// ════════════════════════════════════════════════════════════

export const EventsSchema = z.object({
  discrete: z.array(DiscreteEventSchema),
  faults: z.array(FaultEventSchema),
})

// ════════════════════════════════════════════════════════════
// Observables
// ════════════════════════════════════════════════════════════

export const ObservablesSchema = z.object({
  sample_rate_hz: z.number().positive(),
  channels: z.array(ObservableChannelSchema).min(1),
  kpis: z.array(KPIDefinitionSchema),
})

// ════════════════════════════════════════════════════════════
// Top-Level Contract
// ════════════════════════════════════════════════════════════

export const ScenarioContractSchema = z.object({
  meta: ScenarioMetaSchema,
  stimulus: StimulusSchema,
  ecu: EcuConfigurationSchema,
  events: EventsSchema,
  observables: ObservablesSchema,
})

// ════════════════════════════════════════════════════════════
// Capability Manifest
// ════════════════════════════════════════════════════════════

export const NumericConstraintSchema = z.object({
  min: z.number().optional(),
  max: z.number().optional(),
})

export const EnumConstraintSchema = z.object({
  enum: z.array(z.string()).min(1),
})

export const ConstraintValueSchema = z.union([
  NumericConstraintSchema,
  EnumConstraintSchema,
  z.number(),
])

export const PrimitiveCapabilitySchema = z.object({
  status: PrimitiveSupportStatusSchema,
  since: z.string().nullable(),
  constraints: z.record(z.string(), ConstraintValueSchema),
})

export const CapabilityDeclarationSchema = z.object({
  schema_version: z.string().min(1),
  twin_version: z.string().min(1),
  simulink_model_version: z.string().min(1),
  supported_primitives: z.record(z.string(), PrimitiveCapabilitySchema),
  supported_observables: z.array(z.string()),
  required_for_replay: z.array(z.string()),
})

export const CapabilityManifestSchema = z.object({
  $schema: z.literal('moto-capability/v1'),
  capability: CapabilityDeclarationSchema,
})

// ════════════════════════════════════════════════════════════
// Result Artifacts
// ════════════════════════════════════════════════════════════

export const KPIResultSchema = z.object({
  kpi_id: z.string().min(1),
  measured: z.number(),
  threshold: z.number(),
  threshold_max: z.number().optional(),
  status: z.enum(['PASS', 'FAIL']),
})

export const ScenarioResultSchema = z.object({
  scenario_id: z.string().min(1),
  content_hash: z.string().min(1),
  executed_at: z.string().min(1),
  kpi_results: z.array(KPIResultSchema),
  passed: z.boolean(),
})
